import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Lock } from "lucide-react";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { cn } from "../utils/cn";
import { useNavigate } from "react-router-dom";
import type { AuthFormProps } from "../types/AuthForm.types";

export const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  onSubmit,
  onResetPassword,
  isLoading = false,
  error,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className={cn("w-full max-w-md", className)}>
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-juridico mb-2">
            {mode === "login"
              ? t("auth.login.title")
              : t("auth.resetPassword.title")}
          </h2>
          <p className="text-titan">
            {mode === "login"
              ? t("auth.login.subtitle")
              : t("auth.resetPassword.subtitle")}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder={t("auth.emailPlaceholder")}
            value={formData.email}
            onChange={handleChange("email")}
            leftIcon={<Mail />}
            required
          />

          {mode === "login" && (
            <Input
              type="password"
              placeholder={t("auth.passwordPlaceholder")}
              value={formData.password}
              onChange={handleChange("password")}
              leftIcon={<Lock />}
              isPasswordVisible={isPasswordVisible}
              onTogglePasswordVisibility={() =>
                setIsPasswordVisible(!isPasswordVisible)
              }
              required
            />
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={isLoading}
          >
            {mode === "login"
              ? t("auth.loginButton")
              : t("auth.resetPasswordButton")}
          </Button>
        </form>

        {/* Sign Up Link */}
        {mode === "login" && (
          <Button
            type="button"
            variant="secondary"
            className="w-full mt-4"
            onClick={() => navigate("/onboarding/select-type")}
          >
            {t("auth.orSignUp")}
          </Button>
        )}

        {/* Reset Password Link */}
        {mode === "login" && (
          <p className="text-center mt-6 text-sm text-titan">
            {t("auth.forgotPassword")}{" "}
            <button
              type="button"
              onClick={onResetPassword}
              className="text-tiber-500 hover:underline font-medium"
            >
              {t("auth.recoverPassword")}
            </button>
          </p>
        )}

        {/* Back to Login Link */}
        {mode === "reset-password" && (
          <p className="text-center mt-6 text-sm text-titan">
            {t("auth.rememberPassword")}{" "}
            <button
              type="button"
              onClick={onResetPassword}
              className="text-tiber-500 hover:underline font-medium"
            >
              {t("auth.backToLogin")}
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

AuthForm.displayName = "AuthForm";
